import { track, rotateSession, onBeforePagehide, currentRoute } from './track'

/**
 * STY-725 / spec R6: one builder session per visit to a submission.
 *
 * - builder_session_started carries the progress snapshot and where the vendor
 *   came from.
 * - builder_session_ended records where they were and what was in flight when
 *   they left: on pagehide, on unmount, or after 30 minutes hidden.
 * - If a tab comes back after a 30-minute timeout, it starts a NEW session with
 *   a new session_id, so resumed work never attaches to a session that has
 *   already ended.
 *
 * Never throws: every getter is wrapped, because builder state may be half
 * loaded when the page goes away.
 */

const HIDDEN_TIMEOUT_MS = 30 * 60 * 1000

function browserEnv () {
  return {
    track,
    rotateSession,
    onBeforePagehide,
    currentRoute,
    now: () => Date.now(),
    addListener: (target, type, fn) => (target === 'window' ? window : document).addEventListener(type, fn),
    removeListener: (target, type, fn) => (target === 'window' ? window : document).removeEventListener(type, fn),
    isHidden: () => document.visibilityState === 'hidden',
    setTimeout: (fn, ms) => window.setTimeout(fn, ms),
    clearTimeout: (id) => window.clearTimeout(id)
  }
}

let env = typeof window !== 'undefined' ? browserEnv() : null
let current = null

function safe (fn, fallback) {
  try { return fn() } catch (e) { return fallback }
}

// Runs inside the tracker's pagehide handler, before it stashes and sends, so
// builder_session_ended leads the first unload request.
function onPagehide () {
  if (!current) return
  endBuilderSession('pagehide', { keepListening: true })
}

// bfcache: the page came back without reloading, so start a fresh session.
function onPageshow (event) {
  if (current && current.ended && event && event.persisted) resume()
}

function onVisibility () {
  if (!current) return
  const now = env.now()
  if (env.isHidden()) {
    if (!current.ended && current.visibleSince !== null) {
      current.activeMs += now - current.visibleSince
      current.visibleSince = null
    }
    armHiddenTimeout(now)
  } else {
    if (current.hiddenTimer) env.clearTimeout(current.hiddenTimer)
    current.hiddenTimer = null
    // A sleeping phone can suspend the timer, so check the clock as well: past
    // the timeout means the session ended while hidden (local Codex review).
    if (!current.ended && current.hiddenAt !== null && now - current.hiddenAt >= HIDDEN_TIMEOUT_MS) {
      endBuilderSession('hidden_timeout', { keepListening: true })
    }
    current.hiddenAt = null
    if (current.ended) resume()
    else if (current.visibleSince === null) current.visibleSince = now
  }
}

function armHiddenTimeout (now) {
  current.hiddenAt = now
  if (current.hiddenTimer) env.clearTimeout(current.hiddenTimer)
  current.hiddenTimer = env.setTimeout(() => {
    if (!current) return
    current.hiddenTimer = null
    endBuilderSession('hidden_timeout', { keepListening: true })
  }, HIDDEN_TIMEOUT_MS)
}

function resume () {
  const { submissionId, getSnapshot, getExitState, unsubscribePagehide, onResume } = current
  // A bfcache restore can leave the old 30-minute timer armed; it must not end
  // the resumed session (Codex on #113).
  if (current.hiddenTimer) env.clearTimeout(current.hiddenTimer)
  current.hiddenTimer = null
  safe(() => env.rotateSession())
  begin({ submissionId, entrySource: 'other', getSnapshot, getExitState, onResume, listening: true })
  current.unsubscribePagehide = unsubscribePagehide
  // The page is still scrolled where it was, so let the builder re-report the
  // sections on screen; nothing will scroll into view to do it (local Codex review).
  if (onResume) safe(() => onResume())
}

function begin ({ submissionId, entrySource, getSnapshot, getExitState, onResume, loadOk = true, httpStatus, listening = false }) {
  current = {
    submissionId,
    getSnapshot,
    getExitState,
    onResume,
    hiddenAt: null,
    seen: {},
    lastSection: 'none',
    activeMs: 0,
    visibleSince: safe(() => env.isHidden(), false) ? null : env.now(),
    hiddenTimer: null,
    ended: false,
    listening,
    // The builder's own route: the session can end after the app has already
    // navigated away (unmount runs after the route change) (review on #113).
    route: env.currentRoute ? safe(() => env.currentRoute(), undefined) : undefined
  }
  env.track('builder_session_started', {
    ...safe(() => getSnapshot() || {}, {}),
    entry_source: entrySource,
    load_ok: loadOk,
    http_status: httpStatus
  }, submissionId)

  // Started in a background tab: the hidden clock and the timeout start now,
  // not at the next visibility change (local Codex review).
  if (current.visibleSince === null) armHiddenTimeout(env.now())

  if (!listening) {
    current.unsubscribePagehide = env.onBeforePagehide(onPagehide)
    env.addListener('window', 'pageshow', onPageshow)
    env.addListener('document', 'visibilitychange', onVisibility)
    current.listening = true
  }
}

// Every builder visit is its own session, with its own session_id, even when
// the vendor moves between submissions inside the app (Codex on #113).
export function startBuilderSession (options) {
  safe(() => {
    if (!env) return
    if (current) endBuilderSession('unmount')
    // rotate: false when the builder already started this visit's id at mount.
    if (options.rotate !== false) safe(() => env.rotateSession())
    begin(options)
  })
}

export function noteSectionViewed (section) {
  safe(() => {
    if (!current || current.ended) return
    current.lastSection = section
    if (current.seen[section]) return
    current.seen[section] = true
    env.track('section_viewed', { section }, current.submissionId)
  })
}

export function endBuilderSession (reason, { keepListening = false } = {}) {
  safe(() => {
    if (!current) return
    if (!current.ended) {
      const now = env.now()
      const active = current.activeMs + (current.visibleSince !== null ? now - current.visibleSince : 0)
      current.ended = true
      current.visibleSince = null
      env.track('builder_session_ended', {
        ...safe(() => current.getSnapshot() || {}, {}),
        ...safe(() => current.getExitState() || {}, {}),
        last_section: current.lastSection,
        active_seconds: Math.round(active / 1000),
        end_reason: reason
      }, current.submissionId, current.route !== undefined ? { route: current.route } : undefined)
    }
    if (!keepListening) {
      if (current.hiddenTimer) env.clearTimeout(current.hiddenTimer)
      if (current.unsubscribePagehide) current.unsubscribePagehide()
      env.removeListener('window', 'pageshow', onPageshow)
      env.removeListener('document', 'visibilitychange', onVisibility)
      current = null
      // Ended for good: what the vendor does next (the dashboard, another
      // submission) must not carry the ended session's id (review on #113).
      safe(() => env.rotateSession())
    }
  })
}

export function _resetBuilderSessionForTests (testEnv) {
  env = testEnv
  current = null
}
